<template>
  <div>
    <v-card :class="'auth-ui-card--has-tabs'">
      <div slot="header-title">
        {{ $t('admin.magicLinkFreeTextTitle') }}
      </div>
      <div slot="body">
        <div class="auth-ui-card-feature-description">
          <p>
            {{ $t('admin.magicLinkFreeTextDescription') }}
          </p>
        </div>

        <!-- Template selection -->
        <v-template-section
          :selected-template="selectedTemplate"
          :toggle-modal="toggleTemplateModal"
        />

        <!-- Free-text email input -->
        <div class="field">
          <div class="control">
            <label class="label">{{ $t('admin.magicLinkFreeTextLabel') }}</label>
            <textarea
              v-model="emails"
              class="textarea auth-ui-double-card-textarea"
              rows="10"
              autocomplete="off"
              spellcheck="false"
              @keyup="enable()"
              @click="enable()"
            />
          </div>
          <div class="auth-ui-select-chosen-option-text" />
        </div>
        <div class="field">
          <div class="control auth-ui-row-container">
            <div
              v-if="hasError"
              class="auth-ui-mail-send-message help is-danger"
            >
              {{ error }}
            </div>
            <div
              v-else-if="hasAlarm"
              class="auth-ui-mail-send-message help is-warning"
            >
              {{ success }}
            </div>
            <div
              v-else-if="hasSuccess"
              class="auth-ui-mail-send-message help is-success"
            >
              {{ success }}
            </div>
            <half-circle-spinner
              v-if="showSpinner"
              :animation-duration="1000"
              :size="25"
              :color="'#ff1d5e'"
              class="auth-ui-spinner-wrapper auth-ui-double-card-spinner"
            />
            <button
              :disabled="sendButtonIsDisabled"
              class="button is-link auth-ui-pulled-right has-negative-margin"
              @click.prevent="sendMagicLinksToEmails()"
            >
              {{ $t('admin.magicLinkFreeTextButtonText') }}
            </button>
          </div>
        </div>
      </div>
    </v-card>

    <!-- Modals -->
    <v-template-selection-modal
      ref="templateModal"
      :template-refresh-ready="templateRefreshReady"
      :refresh-templates="refreshTemplates"
      :select-template="selectTemplate"
      :templates="dynamicTemplates"
    />
  </div>
</template>

<script>
/* ============
   * Send One Time Links Free Text Component
   * ============
   *
   * One of the tabs of the Mail Sends component.
   * Used for sending magic links to a manually typed
   * or copied list of email addresses.
   *
   */
import { HalfCircleSpinner } from 'epic-spinners';
import VCard from '@/components/Card.vue';
import Request from '@/tools/Request';
import VTemplateSelectionModal from './CustomComponents/TemplateSelectionModal.vue';
import VTemplateSection from './CustomComponents/TemplateSection.vue';
import ValidationTool from '../../../tools/ValidationTool';

export default {
  name: 'SendOneTimeLinksFreeText',

  components: {
    VCard,
    VTemplateSelectionModal,
    VTemplateSection,
    HalfCircleSpinner,
  },

  props: {
    dynamicTemplates: {
      type: Array,
      default: () => [],
    },
    refreshTemplates: {
      type: Function,
      default: () => {},
    },
    templateRefreshReady: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      hasError: false,
      error: '',
      hasSuccess: false,
      success: '',
      hasAlarm: false,
      isDisabled: true,
      emails: '',
      showSpinner: false,
      selectedTemplate: this.dynamicTemplates.length ? this.dynamicTemplates[0] : {},
    };
  },

  computed: {
    sendButtonIsDisabled() {
      return !this.emails.length || this.isDisabled;
    },
  },

  watch: {
    templateRefreshReady(newVal) {
      if (newVal) {
        if (this.dynamicTemplates.length) {
          this.selectedTemplate = this.dynamicTemplates[0];
        } else {
          this.selectedTemplate = {};
        }
      }
    },
  },

  methods: {
    enable() {
      this.isDisabled = false;
    },

    disable() {
      this.isDisabled = true;
    },

    /**
     * Adds all typed email addresses to a magic link send queue/index.
     */ // eslint-disable-next-line consistent-return
    async sendMagicLinksToEmails() {
      this.disable();
      this.hasError = false;
      this.hasSuccess = false;
      this.hasAlarm = false;

      const emails = this.parseEmailsFromUserInput();

      if (this.emailsAreInvalid(emails)) {
        return;
      }

      this.showSpinner = true;

      try {
        const url = `${process.env.VUE_APP_BACKEND_URL}/send_magic_links`;
        const response = await Request.submit('POST', url, {
          emails: emails.validEmails,
          template: this.selectedTemplate,
        });

        if (response.status && response.status === 'PartialSuccess') {
          this.hasAlarm = true;
          this.success = this.$t('admin.magicLinkFreeTextSendPartialSuccess', { number: emails.validEmails.length - response.failedRecipients.length, list: response.failedRecipients });
        } else {
          this.hasSuccess = true;
          this.success = this.$t('admin.magicLinkFreeTextSendSuccess', { number: emails.validEmails.length });
        }
        this.showSpinner = false;
      } catch (error) {
        this.showSpinner = false;
        this.enable();
        this.error = this.$t('admin.magicLinkGenericSendError');
        this.hasError = true;
      }
    },

    /**
     * Parses user input into an array of email addresses and an array of invalid email addresses.
     * Addresses should be separated by line breaks or commas.
     */
    parseEmailsFromUserInput() {
      const emailArray = this.emails.split(',\n').join(',')
        .split('\n').join(',') // eslint-disable-next-line newline-per-chained-call
        .split(', ').join(',') // eslint-disable-next-line newline-per-chained-call
        .split(' ,').join(',')
        .split(',');

      return {
        validEmails: emailArray.filter((email) => email.length),
        invalidEmails: emailArray.filter((email) => !ValidationTool.validateEmail(email)),
      };
    },

    /**
     * Returns true and shows an error in the UI if the given emails object
     * contains invalid emails or if the length of its valid emails is larger than 1000.
     */
    emailsAreInvalid(emails) {
      if (emails.invalidEmails.length) {
        this.error = `${this.$t('admin.magicLinkInvalidAddresses')} ${emails.invalidEmails.join(', ')}`;
        this.hasError = true;
        return true;
      }

      if (emails.validEmails.length > 1000) {
        this.error = this.$t('admin.magicLinkTooManyRecipientsError', { number: emails.validEmails.length });
        this.hasError = true;
        return true;
      }

      return false;
    },

    toggleTemplateModal() {
      this.$refs.templateModal.toggleModal(this.dynamicTemplates);
    },

    selectTemplate(template) {
      this.selectedTemplate = template;
    },
  },
};
</script>
